import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import type { ClassificationRule } from "@/services/api/classification-rules";

interface ClassificationRulesTableRowProps {
  rule: ClassificationRule;
  onEdit: (rule: ClassificationRule) => void;
  onDelete: (rule: ClassificationRule) => void;
}

export function ClassificationRulesTableRow({
  rule,
  onEdit,
  onDelete,
}: ClassificationRulesTableRowProps) {
  return (
    <tr className="hover:bg-grid-50 transition-colors">
      <td className="px-6 py-4">
        <div className="font-medium text-ink-900">{rule.category}</div>
      </td>
      <td className="px-6 py-4">
        <div className="flex flex-wrap gap-1">
          {rule.targets && rule.targets.length > 0 ? (
            rule.targets.map((target, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {target}
              </Badge>
            ))
          ) : (
            <span className="text-sm text-ink-400">-</span>
          )}
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-ink-700">{rule.value}</div>
      </td>
      <td className="px-6 py-4">
        <span className="text-sm text-ink-700">{rule.weight ?? 0}</span>
      </td>
      <td className="px-6 py-4">
        <div className="flex flex-wrap gap-1">
          {rule.keywords && rule.keywords.length > 0 ? (
            rule.keywords.slice(0, 3).map((keyword, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {keyword}
              </Badge>
            ))
          ) : (
            <span className="text-xs text-ink-400">No keywords</span>
          )}
          {rule.keywords && rule.keywords.length > 3 && (
            <Badge variant="outline" className="text-xs">
              +{rule.keywords.length - 3} more
            </Badge>
          )}
        </div>
      </td>
      <td className="px-6 py-4">
        <span className="inline-flex items-center gap-2 text-sm text-ink-700">
          <span
            className={`h-2 w-2 rounded-full ${
              rule.isActive ? "bg-emerald-500" : "bg-grid-300"
            }`}
          />
          {rule.isActive ? "Active" : "Inactive"}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(rule)}
            className="h-8 w-8"
          >
            <Pencil className="h-4 w-4" />
            <span className="sr-only">Edit</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(rule)}
            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
            <span className="sr-only">Delete</span>
          </Button>
        </div>
      </td>
    </tr>
  );
}
